import type {
  AutonomyMode,
  PermissionDecision,
  PermissionInput,
  PermissionLevel,
  PermissionResult,
} from "@haulbase/headless-contracts";

export interface PolicyCheckRecord {
  ruleId: string;
  outcome: PermissionDecision;
  reason: string;
}

export type PolicyRow = {
  currentMode?: AutonomyMode;
  defaultMode?: AutonomyMode;
  permissionLevel?: PermissionLevel;
  defaultPermissionLevel?: PermissionLevel;
  bypassExpiresAt?: number;
  bypassEnabledBy?: string;
  bypassReason?: string;
  nonBypassableRules?: string[];
  customerOverride?: {
    customerId: string;
    grantedMode?: AutonomyMode;
    permissionLevel?: PermissionLevel;
    grantedAt: number;
  };
};

export type PolicyEvaluationResult = PermissionResult & {
  policyChecks: PolicyCheckRecord[];
};

const EXTERNAL_COMMIT_ACTIONS = new Set<string>([
  "share_customer_performance_report",
  "book_carrier",
  "tender_carrier",
  "approve_accessorial",
  "override_compliance",
  "create_load",
  "cancel_load",
  "split_load",
  "generate_spot_quote",
  "send_customer_quote",
  "apply_accessorial",
  "generate_rate_confirmation",
  "send_tender_email",
  "send_tender_edi204",
  "capture_carrier_acceptance",
  "generate_bol",
  "generate_customer_invoice",
  "run_carrier_settlement_batch",
  "apply_quick_pay_discount",
  "redirect_payment_to_factor",
  "place_carrier_payment_hold",
  "release_carrier_payment_hold",
  "start_detention_clock",
  "bill_tonu",
  "open_osd_claim",
  "open_cargo_claim",
  "open_customer_dispute",
  "record_noa",
  "add_carrier_to_dnu",
  "push_accounting_transaction",
]);

const LOAD_BOARD_POSTING_ACTIONS = new Set<string>(["post_load", "update_posting", "expire_posting"]);

const DISPATCH_LIKE_ACTIONS = new Set<string>([
  "book_carrier",
  "tender_carrier",
  "generate_rate_confirmation",
  "send_tender_email",
  "send_tender_edi204",
  "capture_carrier_acceptance",
  "assign_driver_truck_trailer",
]);

export function evaluatePolicy(policy: PolicyRow | null, input: PermissionInput): PolicyEvaluationResult {
  const effectiveMode = resolveEffectiveMode(policy, input);
  const effectivePermissionLevel = input.permissionLevel ?? resolveEffectivePermissionLevel(policy, input);
  const requestedAutonomyMode = input.autonomyMode ?? effectiveMode;
  const requestedPermissionLevel = input.permissionLevel ?? effectivePermissionLevel;
  const checks: PolicyCheckRecord[] = [];

  addModeChecks(checks, effectiveMode, effectivePermissionLevel);

  if (input.riskScore >= 80 || input.confidenceScore < 35) {
    checks.push({
      ruleId: "risk_confidence_emergency_lock",
      outcome: "block",
      reason: "risk/confidence requires emergency lock",
    });
  }

  if (effectivePermissionLevel === "bypass_permissions") {
    if (typeof policy?.bypassExpiresAt === "number" && policy.bypassExpiresAt <= Date.now()) {
      checks.push({
        ruleId: "bypass_expired",
        outcome: "block",
        reason: "bypass permission window has expired",
      });
    }
  }

  if (
    effectivePermissionLevel === "auto_mode" &&
    (input.riskScore >= 60 || input.confidenceScore < 60)
  ) {
    checks.push({
      ruleId: "auto_execute_low_risk_bounds",
      outcome: "approval_required",
      reason: "auto-execute requires risk below 60 and confidence at least 60",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && EXTERNAL_COMMIT_ACTIONS.has(input.actionType)) {
    checks.push({
      ruleId: "external_commit_requires_approval",
      outcome: "approval_required",
      reason: "money, customer-facing, or external-commit actions require approval",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && input.audience === "customer") {
    checks.push({
      ruleId: "customer_facing_requires_approval",
      outcome: "approval_required",
      reason: "customer-facing actions require approval",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && input.audience === "factor") {
    checks.push({
      ruleId: "factor_facing_requires_approval",
      outcome: "approval_required",
      reason: "factor-facing actions require approval",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && LOAD_BOARD_POSTING_ACTIONS.has(input.actionType)) {
    checks.push({
      ruleId: "load_board_posting_requires_approval",
      outcome: "approval_required",
      reason: "load-board posting changes require approval",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && input.customerSensitive) {
    checks.push({
      ruleId: "customer_sensitive_requires_approval",
      outcome: "approval_required",
      reason: "sensitive customer policy requires approval",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && input.financialExposureCents > 0) {
    checks.push({
      ruleId: "financial_exposure_requires_approval",
      outcome: "approval_required",
      reason: "financial exposure requires approval",
    });
  }

  if (
    effectivePermissionLevel !== "bypass_permissions" &&
    typeof input.carrierAuthorityAgeDays === "number" &&
    input.carrierAuthorityAgeDays < 180
  ) {
    checks.push({
      ruleId: "carrier_authority_age_under_180_days",
      outcome: "approval_required",
      reason: "carrier authority age under 180 days requires approval",
    });
  }

  if (
    effectivePermissionLevel !== "bypass_permissions" &&
    typeof input.cargoValueCents === "number" &&
    input.cargoValueCents > 10_000_000
  ) {
    checks.push({
      ruleId: "cargo_value_over_100k",
      outcome: "approval_required",
      reason: "cargo value over $100,000 requires enhanced verification approval",
    });
  }

  if (effectivePermissionLevel !== "bypass_permissions" && typeof input.marginPercent === "number" && input.marginPercent < 12) {
    checks.push({
      ruleId: "margin_under_12_percent",
      outcome: "approval_required",
      reason: "margin below 12% requires manager approval",
    });
  }

  if (input.riskScore >= 75 && DISPATCH_LIKE_ACTIONS.has(input.actionType)) {
    checks.push({
      ruleId: "high_carrier_risk_blocks_dispatch",
      outcome: "block",
      reason: "high carrier risk blocks dispatch-like actions",
    });
  }

  const blockingCheck = checks.find((check) => check.outcome === "block");
  if (blockingCheck) {
    return buildResult("block", requestedAutonomyMode, effectiveMode, requestedPermissionLevel, effectivePermissionLevel, checks);
  }

  const approvalCheck = checks.find((check) => check.outcome === "approval_required");
  if (approvalCheck) {
    return buildResult(
      "approval_required",
      requestedAutonomyMode,
      effectiveMode === "auto_execute_low_risk" ? "ask_before_acting" : effectiveMode,
      requestedPermissionLevel,
      effectivePermissionLevel === "auto_mode" ? "ask_for_permission" : effectivePermissionLevel,
      checks,
    );
  }

  checks.push({
    ruleId: "within_granted_autonomy",
    outcome: "allow",
    reason: "action is within tenant policy and granted autonomy",
  });
  return buildResult("allow", requestedAutonomyMode, effectiveMode, requestedPermissionLevel, effectivePermissionLevel, checks);
}

function resolveEffectiveMode(policy: PolicyRow | null, input: PermissionInput): AutonomyMode {
  if (policy?.customerOverride?.customerId && policy.customerOverride.customerId === input.customerId && policy.customerOverride.grantedMode) {
    return policy.customerOverride.grantedMode;
  }
  if (input.permissionLevel === "auto_mode") return "auto_execute_low_risk";
  if (input.permissionLevel === "bypass_permissions") return "auto_execute_low_risk";
  if (input.permissionLevel === "ask_for_permission") return "ask_before_acting";
  return policy?.currentMode ?? modeForPermissionLevel(policy?.permissionLevel) ?? "ask_before_acting";
}

function resolveEffectivePermissionLevel(policy: PolicyRow | null, input: PermissionInput): PermissionLevel {
  if (policy?.customerOverride?.customerId && policy.customerOverride.customerId === input.customerId) {
    if (policy.customerOverride.permissionLevel) return policy.customerOverride.permissionLevel;
    if (policy.customerOverride.grantedMode) return permissionLevelForMode(policy.customerOverride.grantedMode);
  }
  return policy?.permissionLevel ?? permissionLevelForMode(policy?.currentMode ?? policy?.defaultMode ?? "ask_before_acting");
}

function modeForPermissionLevel(level?: PermissionLevel): AutonomyMode | undefined {
  if (level === "ask_for_permission") return "ask_before_acting";
  if (level === "auto_mode" || level === "bypass_permissions") return "auto_execute_low_risk";
  return undefined;
}

function permissionLevelForMode(mode: AutonomyMode): PermissionLevel {
  if (mode === "auto_execute_low_risk") return "auto_mode";
  return "ask_for_permission";
}

function addModeChecks(checks: PolicyCheckRecord[], mode: AutonomyMode, permissionLevel: PermissionLevel) {
  if (mode === "observe" || permissionLevel === "ask_for_permission") {
    checks.push({
      ruleId: permissionLevel === "ask_for_permission" ? "permission_ask_for_permission" : "mode_observe",
      outcome: "approval_required",
      reason: "policy requires approval before acting",
    });
  }
  if (mode === "draft") {
    checks.push({
      ruleId: "mode_draft",
      outcome: "approval_required",
      reason: "policy requires draft-and-hold",
    });
  }
  if (mode === "ask_before_acting") {
    checks.push({
      ruleId: "mode_ask_before_acting",
      outcome: "approval_required",
      reason: "policy requires approval before acting",
    });
  }
  if (mode === "emergency_lock") {
    checks.push({
      ruleId: "mode_emergency_lock",
      outcome: "block",
      reason: "policy is emergency locked",
    });
  }
  if (permissionLevel === "auto_mode") {
    checks.push({
      ruleId: "permission_auto_mode",
      outcome: "allow",
      reason: "auto mode may execute actions that pass policy checks",
    });
  }
  if (permissionLevel === "bypass_permissions") {
    checks.push({
      ruleId: "permission_bypass_permissions",
      outcome: "allow",
      reason: "bypass permissions skips business approval gates but not non-bypassable safety checks",
    });
  }
}

function buildResult(
  decision: PermissionDecision,
  requestedAutonomyMode: AutonomyMode,
  grantedAutonomyMode: AutonomyMode,
  requestedPermissionLevel: PermissionLevel,
  grantedPermissionLevel: PermissionLevel,
  policyChecks: PolicyCheckRecord[],
): PolicyEvaluationResult {
  const reasons = policyChecks.map((check) => check.reason);
  return {
    decision,
    requestedAutonomyMode,
    grantedAutonomyMode,
    requestedPermissionLevel,
    grantedPermissionLevel,
    reasons: reasons.length > 0 ? [...new Set(reasons)] : ["action is within granted autonomy"],
    policyChecks,
  };
}
